import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { Search, Globe, GraduationCap, ExternalLink, Copy, Check, Image as ImageIcon, ChevronLeft, ChevronRight, RefreshCw, BarChart3, AlertTriangle, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import CitationPopup from '../../components/CitationPopup'
import AgentHeader from '../../components/AgentHeader'

const SearchResultPage = ({ query, searchResult, searchType, onBack, onRetry }) => {
  console.log('🔍 SearchResultPage received props:', {
    query,
    searchResult,
    searchType,
    sources: searchResult?.sources,
    images: searchResult?.images
  })
  
  const navigate = useNavigate()
  const [copiedText, setCopiedText] = useState('')
  const [showCitation, setShowCitation] = useState(false)
  const [selectedCitation, setSelectedCitation] = useState(null)
  const [citationPosition, setCitationPosition] = useState({ x: 0, y: 0 })
  const [showAllSources, setShowAllSources] = useState(false)
  const [showAllImages, setShowAllImages] = useState(false)
  const sourcesScrollRef = useRef(null)
  const imagesScrollRef = useRef(null)

  // Handle navigation to search agent
  const handleSearchMore = () => {
    navigate('/', { state: { selectedAgentId: 'search_agent' } })
  }

  // Handle citation click
  const handleCitationClick = (event, citationNumber) => {
    event.preventDefault()
    const source = searchResult.sources.find(s => s.id === citationNumber)
    if (source) {
      setSelectedCitation(source)
      setCitationPosition({ x: event.clientX, y: event.clientY })
      setShowCitation(true)
    }
  }

  // Process markdown text and handle citations
  const processMarkdownWithCitations = (text) => {
    if (!text) return ''
    
    // Only convert citations that have valid sources
    return text.replace(/\[(\d+)\]/g, (match, citationNumber) => {
      const citationNum = parseInt(citationNumber)
      const hasValidSource = searchResult.sources && searchResult.sources.find(s => s.id === citationNum)
      
      if (hasValidSource) {
        return `[[$1]](#citation-${citationNumber})`
      } else {
        // Remove citation if no valid source exists
        return ''
      }
    })
  }

  // Convert markdown to structured plain text while preserving formatting
  const markdownToStructuredText = (markdown) => {
    if (!markdown) return ''
    
    return markdown
      // Convert headers to structured text with proper formatting
      .replace(/^#{1}\s+(.+)$/gm, '\n\n=== $1 ===\n')
      .replace(/^#{2}\s+(.+)$/gm, '\n\n--- $1 ---\n')
      .replace(/^#{3}\s+(.+)$/gm, '\n\n▶ $1\n')
      .replace(/^#{4}\s+(.+)$/gm, '\n\n• $1\n')
      .replace(/^#{5}\s+(.+)$/gm, '\n\n$1\n')
      .replace(/^#{6}\s+(.+)$/gm, '\n\n$1\n')
      // Convert bold formatting to UPPERCASE for emphasis
      .replace(/\*\*([^*]+)\*\*/g, (match, text) => text.toUpperCase())
      .replace(/__([^_]+)__/g, (match, text) => text.toUpperCase())
      // Keep italic with visual markers
      .replace(/\*([^*]+)\*/g, '_$1_')
      .replace(/_([^_]+)_/g, '_$1_')
      // Convert code blocks to indented text with clear formatting
      .replace(/```([\s\S]*?)```/g, '\n\n[KODE]\n$1\n[/KODE]\n')
      .replace(/`([^`]+)`/g, '"$1"')
      // Convert links to "text (url)" format
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
      // Remove citations in brackets but keep the content
      .replace(/\[\[(\d+)\]\]\(#citation-\d+\)/g, '[$1]')
      .replace(/\[\d+\]/g, '')
      // Convert unordered lists to bullet points with proper spacing
      .replace(/^[-*+]\s+(.+)$/gm, '\n• $1')
      // Convert ordered lists to numbered format with proper spacing
      .replace(/^(\d+)\.\s+(.+)$/gm, '\n$1. $2')
      // Handle nested lists (add proper indentation)
      .replace(/^\s{2,4}[-*+]\s+(.+)$/gm, '\n  • $1')
      .replace(/^\s{2,4}(\d+)\.\s+(.+)$/gm, '\n  $1. $2')
      .replace(/^\s{4,6}[-*+]\s+(.+)$/gm, '\n    • $1')
      .replace(/^\s{4,6}(\d+)\.\s+(.+)$/gm, '\n    $1. $2')
      // Handle blockquotes
      .replace(/^>\s+(.+)$/gm, '\n"$1"')
      // Handle horizontal rules
      .replace(/^---+$/gm, '\n' + '='.repeat(50) + '\n')
      // Add proper spacing for paragraphs
      .replace(/\n\n\n+/g, '\n\n')
      // Clean up extra whitespace
      .replace(/^\s+|\s+$/g, '')
      // Ensure proper line breaks after headers and sections
      .replace(/(===.*===)\n/g, '$1\n\n')
      .replace(/(---.*---)\n/g, '$1\n\n')
      .replace(/(▶.*?)\n/g, '$1\n\n')
  }

  // Copy text to clipboard with rich formatting using HTML and plain text
  const copyToClipboard = async (text, type) => {
    try {
      const currentDate = new Date().toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
      
      // Create HTML version with proper formatting
      let htmlContent = `
        <div style="font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; line-height: 1.6; color: #000;">
          <div style="border: 2px solid #000; padding: 20px; margin-bottom: 20px; background: #f9f9f9;">
            <h1 style="margin: 0; color: #000; text-align: center; font-size: 24px;">HASIL PENCARIAN - CARA SEARCH AGENT</h1>
            <hr style="border: 1px solid #000; margin: 15px 0;">
            <p style="margin: 5px 0;"><strong>PERTANYAAN:</strong> ${query}</p>
            <p style="margin: 5px 0;"><strong>TANGGAL:</strong> ${currentDate}</p>
            <p style="margin: 5px 0;"><strong>SUMBER:</strong> Cara Search Agent - Pencarian Terpercaya</p>
          </div>
          
          <div style="margin: 20px 0;">
            ${convertMarkdownToHTML(text)}
          </div>
      `
      
      // Add references section in HTML
      if (searchResult.sources && searchResult.sources.length > 0) {
        htmlContent += `
          <div style="border-top: 2px solid #000; padding-top: 20px; margin-top: 30px;">
            <h2 style="color: #000; border-bottom: 2px solid #000; padding-bottom: 10px;">DAFTAR REFERENSI</h2>
            <ol style="padding-left: 20px;">
        `
        
        searchResult.sources.forEach((source, index) => {
          const currentYear = new Date().getFullYear()
          const domain = source.url ? new URL(source.url).hostname.replace('www.', '') : 'Unknown'
          
          htmlContent += `
            <li style="margin: 15px 0; padding: 10px; background: #f9f9f9; border-left: 4px solid #000;">
              <strong style="color: #000;">${source.title}</strong><br>
              <span style="color: #000;">Sumber: ${domain} | Tahun: ${currentYear}</span><br>
              <a href="${source.url}" style="color: #000; text-decoration: underline;">🔗 ${source.url}</a>
            </li>
          `
        })
        
        htmlContent += `</ol></div>`
      }
      
      // Add footer
      htmlContent += `
          <div style="border-top: 2px solid #000; padding-top: 20px; margin-top: 30px; background: #f9f9f9; padding: 15px; border-radius: 8px;">
            <h3 style="color: #000; margin-top: 0;">DISCLAIMER</h3>
            <p style="margin: 5px 0; font-size: 14px; color: #000;">Informasi ini dikumpulkan dari berbagai sumber terpercaya melalui Cara Search Agent.</p>
            <p style="margin: 5px 0; font-size: 14px; color: #000;">Selalu verifikasi informasi penting dengan sumber asli.</p>
            <p style="margin: 5px 0; font-size: 14px; color: #000;">Untuk informasi terbaru, silakan kunjungi sumber referensi yang tercantum.</p>
            <hr style="border: 1px solid #000; margin: 10px 0;">
            <p style="margin: 0; font-size: 12px; color: #000; text-align: center;">Generated by Cara Search Agent - ${currentDate}</p>
          </div>
        </div>
      `
      
      // Create plain text fallback
      let plainText = "=".repeat(60) + "\n"
      plainText += "HASIL PENCARIAN - CARA SEARCH AGENT\n"
      plainText += "=".repeat(60) + "\n\n"
      plainText += `PERTANYAAN: ${query}\n`
      plainText += `TANGGAL: ${currentDate}\n`
      plainText += `SUMBER: Cara Search Agent - Pencarian Terpercaya\n\n`
      plainText += "=".repeat(60) + "\n\n"
      plainText += markdownToStructuredText(text)
      
      if (searchResult.sources && searchResult.sources.length > 0) {
        plainText += "\n\n" + "=".repeat(50) + "\n"
        plainText += "=== DAFTAR REFERENSI ===\n"
        plainText += "=".repeat(50) + "\n\n"
        
        searchResult.sources.forEach((source, index) => {
          const currentYear = new Date().getFullYear()
          const domain = source.url ? new URL(source.url).hostname.replace('www.', '') : 'Unknown'
          plainText += `[${index + 1}] ${source.title.toUpperCase()}\n`
          plainText += `    Sumber: ${domain}\n`
          plainText += `    Tahun: ${currentYear}\n`
          plainText += `    URL: ${source.url}\n\n`
        })
        
        plainText += "=".repeat(50) + "\n"
      }
      
      plainText += "\n\n" + "=".repeat(60) + "\n"
      plainText += "DISCLAIMER\n"
      plainText += "=".repeat(60) + "\n\n"
      plainText += "Informasi ini dikumpulkan dari berbagai sumber terpercaya melalui Cara Search Agent.\n"
      plainText += "Selalu verifikasi informasi penting dengan sumber asli.\n"
      plainText += "Untuk informasi terbaru, silakan kunjungi sumber referensi yang tercantum.\n\n"
      plainText += "Generated by Cara Search Agent - " + currentDate + "\n"
      plainText += "=".repeat(60)
      
      // Try to copy both HTML and plain text
      if (navigator.clipboard && window.ClipboardItem) {
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' })
        })
        await navigator.clipboard.write([clipboardItem])
      } else {
        // Fallback to plain text only
        await navigator.clipboard.writeText(plainText)
      }
      
      setCopiedText(type)
      setTimeout(() => setCopiedText(''), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
      // Final fallback - copy plain text without formatting
      try {
        await navigator.clipboard.writeText(text)
        setCopiedText(type)
        setTimeout(() => setCopiedText(''), 2000)
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr)
      }
    }
  }
  
  // Helper function to convert markdown to HTML
  const convertMarkdownToHTML = (markdown) => {
    if (!markdown) return ''
    
    return markdown
      // Convert headers
      .replace(/^#{1}\s+(.+)$/gm, '<h1 style="color: #000; font-size: 28px; margin: 25px 0 15px 0; border-bottom: 3px solid #000; padding-bottom: 10px;">$1</h1>')
      .replace(/^#{2}\s+(.+)$/gm, '<h2 style="color: #000; font-size: 24px; margin: 20px 0 12px 0; border-bottom: 2px solid #000; padding-bottom: 8px;">$1</h2>')
      .replace(/^#{3}\s+(.+)$/gm, '<h3 style="color: #000; font-size: 20px; margin: 18px 0 10px 0; border-left: 4px solid #000; padding-left: 15px;">$1</h3>')
      .replace(/^#{4}\s+(.+)$/gm, '<h4 style="color: #000; font-size: 18px; margin: 15px 0 8px 0;">• $1</h4>')
      .replace(/^#{5}\s+(.+)$/gm, '<h5 style="color: #000; font-size: 16px; margin: 12px 0 6px 0;">$1</h5>')
      .replace(/^#{6}\s+(.+)$/gm, '<h6 style="color: #000; font-size: 14px; margin: 10px 0 5px 0;">$1</h6>')
      // Convert bold
      .replace(/\*\*([^*]+)\*\*/g, '<strong style="color: #000; font-weight: bold;">$1</strong>')
      .replace(/__([^_]+)__/g, '<strong style="color: #000; font-weight: bold;">$1</strong>')
      // Convert italic
      .replace(/\*([^*]+)\*/g, '<em style="color: #000; font-style: italic;">$1</em>')
      .replace(/_([^_]+)_/g, '<em style="color: #000; font-style: italic;">$1</em>')
      // Convert code blocks
      .replace(/```([\s\S]*?)```/g, '<pre style="background: #f9f9f9; border: 1px solid #000; border-radius: 6px; padding: 15px; margin: 15px 0; overflow-x: auto; font-family: \'Courier New\', monospace;"><code style="color: #000;">$1</code></pre>')
      .replace(/`([^`]+)`/g, '<code style="background: #f9f9f9; color: #000; padding: 2px 6px; border-radius: 4px; font-family: \'Courier New\', monospace;">$1</code>')
      // Convert links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #000; text-decoration: underline;">$1</a>')
      // Remove citations
      .replace(/\[\[(\d+)\]\]\(#citation-\d+\)/g, '<sup style="color: #000; font-weight: bold;">[$1]</sup>')
      .replace(/\[\d+\]/g, '')
      // Convert lists
      .replace(/^[-*+]\s+(.+)$/gm, '<li style="margin: 5px 0; padding-left: 5px;">$1</li>')
      .replace(/^(\d+)\.\s+(.+)$/gm, '<li style="margin: 5px 0; padding-left: 5px;">$2</li>')
      // Convert blockquotes
      .replace(/^>\s+(.+)$/gm, '<blockquote style="border-left: 4px solid #000; margin: 15px 0; padding: 10px 20px; background: #f9f9f9; font-style: italic; color: #000;">$1</blockquote>')
      // Convert horizontal rules
      .replace(/^---+$/gm, '<hr style="border: none; border-top: 2px solid #000; margin: 25px 0;">')
      // Convert paragraphs
      .replace(/\n\n/g, '</p><p style="margin: 12px 0; line-height: 1.6; color: #000;">')
      // Wrap in paragraph tags
      .replace(/^/, '<p style="margin: 12px 0; line-height: 1.6; color: #000;">')
      .replace(/$/, '</p>')
      // Wrap lists in ul/ol tags
      .replace(/(<li[^>]*>.*?<\/li>)/gs, (match) => {
        if (match.includes('1.') || match.includes('2.') || match.includes('3.')) {
          return `<ol style="margin: 15px 0; padding-left: 25px;">${match}</ol>`
        } else {
          return `<ul style="margin: 15px 0; padding-left: 25px;">${match}</ul>`
        }
      })
  }

  // Refs for sections (keeping for potential future use)
  // const scrollSources = (direction) => { ... }
  // const scrollImages = (direction) => { ... }

  return (
    <div className="min-h-screen bg-bg-primary overflow-x-hidden">
      <AgentHeader
        onBack={onBack}
        icon={Search}
        title="Search Agent"
        query="Hasil pencarian dari sumber terpercaya"
        iconColor="text-brand-primary"
        iconBgColor="bg-brand-primary/10"
        actions={[
          {
            icon: copiedText === 'answer' ? Check : Copy,
            label: copiedText === 'answer' ? "Tersalin!" : "Salin",
            onClick: () => copyToClipboard(searchResult.answer, 'answer'),
            variant: "secondary",
            className: copiedText === 'answer' ? "text-green-500" : ""
          },
          {
            icon: Search,
            label: "Cari Lainnya",
            onClick: handleSearchMore,
            variant: "primary"
          }
        ]}
      />

      <div className="max-w-4xl mx-auto px-2 sm:px-4 py-3 sm:py-6">
          <div className="bg-bg-quaternary rounded-lg p-4 mb-6">
            <p className="text-brand-primary font-medium mb-2 text-base">Pertanyaan:</p>
            <p className="text-text-primary text-base leading-relaxed">{query}</p>
          </div>

          {/* Sources Section */}
        {searchResult.sources && searchResult.sources.length > 0 && (
          <div className="mb-2.5 sm:mb-6">
            <div className="mb-2.5 sm:mb-4">
              <h2 className="text-sm sm:text-xl font-bold text-text-primary">Sumber Referensi</h2>
            </div>
            
            <div className="relative">
              <div 
                ref={sourcesScrollRef}
                className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 transition-all duration-300 ${!showAllSources ? 'overflow-hidden' : ''}`}
                style={!showAllSources ? { maxHeight: '240px' } : {}}
              >
                {(showAllSources ? searchResult.sources : searchResult.sources.slice(0, 6)).map((source) => {
                  // Extract domain from URL for favicon
                  const getDomainFromUrl = (url) => {
                    try {
                      const domain = new URL(url).hostname
                      return domain.replace('www.', '')
                    } catch {
                      return 'unknown'
                    }
                  }
                  
                  const domain = getDomainFromUrl(source.url)
                  const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=32`
                  
                  return (
                  <div
                    key={source.id}
                    className="w-full bg-bg-quaternary rounded-md sm:rounded-lg p-2 sm:p-4 hover:bg-bg-tertiary transition-colors min-h-[90px] sm:min-h-[120px] flex flex-col"
                  >
                    <div className="flex items-start space-x-1 sm:space-x-3 flex-1">
                      <div className="flex-shrink-0">
                        <img 
                          src={faviconUrl}
                          alt={`${domain} favicon`}
                          className="w-3 h-3 sm:w-6 sm:h-6 rounded"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'block'
                          }}
                        />
                        <div className="w-3 h-3 sm:w-6 sm:h-6 bg-brand-primary/10 rounded flex items-center justify-center" style={{display: 'none'}}>
                          <Globe className="w-2 h-2 sm:w-4 sm:h-4 text-brand-primary" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                          <div className="text-xs sm:text-sm font-medium text-text-primary truncate">
                            {domain}
                          </div>
                          {source.isFallback && (
                            <span className="text-xs text-amber-400 bg-amber-900 px-1 py-0.5 rounded">
                              Fallback
                            </span>
                          )}
                        </div>
                        <div className="text-xs sm:text-sm font-semibold text-text-primary leading-tight line-clamp-2 flex-1">
                          {source.title}
                        </div>
                      </div>
                    </div>
                    <div className="mt-1 sm:mt-2 pt-1 sm:pt-2 border-t border-border-secondary">
                      <a 
                        href={source.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-xs text-brand-primary hover:text-brand-secondary transition-colors"
                      >
                        <ExternalLink className="w-2 h-2 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                        <span className="hidden sm:inline">Buka Sumber</span>
                        <span className="sm:hidden">Buka</span>
                      </a>
                    </div>
                  </div>
                )
              })}
              </div>
              
              {/* Overlay gradasi dan tombol Tampilkan semua untuk Sources */}
              {!showAllSources && searchResult.sources.length > 6 && (
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-bg-primary via-bg-primary/80 to-transparent flex items-end justify-center pb-4">
                  <button
                    onClick={() => setShowAllSources(true)}
                    className="px-4 py-2 bg-brand-primary hover:bg-brand-secondary text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2 shadow-lg"
                  >
                    <span>Tampilkan Semua ({searchResult.sources.length})</span>
                  </button>
                </div>
              )}
              
              {/* Tombol Tampilkan lebih sedikit */}
              {showAllSources && searchResult.sources.length > 6 && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={() => setShowAllSources(false)}
                    className="px-4 py-2 bg-bg-quaternary hover:bg-bg-secondary text-text-primary rounded-lg transition-colors font-medium text-sm"
                  >
                    Tampilkan Lebih Sedikit
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Images Section */}
        {searchResult.images && searchResult.images.length > 0 && (
          <div className="mb-2.5 sm:mb-6">
            <div className="mb-2.5 sm:mb-4">
              <h2 className="text-sm sm:text-xl font-bold text-text-primary">Gambar Terkait</h2>
            </div>
            
            <div className="relative">
              <div 
                ref={imagesScrollRef}
                className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4 transition-all duration-300 ${!showAllImages ? 'overflow-hidden' : ''}`}
                style={!showAllImages ? { maxHeight: '320px' } : {}}
              >
                {(showAllImages ? searchResult.images.filter((image) => image.url && !image.url.includes('placeholder') && !image.url.includes('default')) : searchResult.images.filter((image) => image.url && !image.url.includes('placeholder') && !image.url.includes('default')).slice(0, 8)).map((image) => (
                <div
                  key={image.id}
                  className="w-full bg-bg-quaternary rounded-md sm:rounded-lg overflow-hidden hover:bg-bg-tertiary transition-colors"
                >
                  <div className="aspect-video bg-bg-secondary flex items-center justify-center">
                    {image.url ? (
                      <img
                        src={image.url}
                        alt={image.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'flex'
                        }}
                      />
                    ) : null}
                    <div className="hidden w-full h-full items-center justify-center text-text-tertiary">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  </div>
                  
                  <div className="p-1.5 sm:p-3">
                    <h3 className="font-medium text-text-primary text-xs sm:text-sm mb-1 line-clamp-2">
                      {image.title}
                    </h3>
                    
                    {image.description && (
                      <p className="text-xs text-text-secondary mb-1 sm:mb-2 line-clamp-2">
                        {image.description}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-text-tertiary truncate flex-1 mr-1">
                        {image.source}
                      </span>
                      {image.url && (
                        <a
                          href={image.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-0.5 text-xs text-brand-primary hover:text-brand-secondary transition-colors flex-shrink-0"
                        >
                          <span>Lihat</span>
                          <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
              
              {/* Overlay gradasi dan tombol Tampilkan semua untuk Images */}
              {!showAllImages && searchResult.images.filter((image) => image.url && !image.url.includes('placeholder') && !image.url.includes('default')).length > 8 && (
                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-bg-primary via-bg-primary/80 to-transparent flex items-end justify-center pb-4">
                  <button
                    onClick={() => setShowAllImages(true)}
                    className="px-4 py-2 bg-brand-primary hover:bg-brand-secondary text-white rounded-lg transition-colors font-medium text-sm flex items-center gap-2 shadow-lg"
                  >
                    <span>Tampilkan Semua ({searchResult.images.filter((image) => image.url && !image.url.includes('placeholder') && !image.url.includes('default')).length})</span>
                  </button>
                </div>
              )}
              
              {/* Tombol Tampilkan lebih sedikit */}
              {showAllImages && searchResult.images.filter((image) => image.url && !image.url.includes('placeholder') && !image.url.includes('default')).length > 8 && (
                <div className="flex justify-center mt-4">
                  <button
                    onClick={() => setShowAllImages(false)}
                    className="px-4 py-2 bg-bg-quaternary hover:bg-bg-secondary text-text-primary rounded-lg transition-colors font-medium text-sm"
                  >
                    Tampilkan Lebih Sedikit
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Main Answer - Moved to last position */}
        <div className="bg-bg-quaternary rounded-lg sm:rounded-xl p-2.5 sm:p-6">
          <div className="mb-2.5 sm:mb-4 flex items-center justify-between">
            <h2 className="text-sm sm:text-xl font-bold text-text-primary">Jawaban</h2>
            {searchResult.isStreamingAnswer && (
              <div className="flex items-center gap-2 text-brand-primary">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-primary"></div>
                <span className="text-xs sm:text-sm">Sedang menulis jawaban...</span>
              </div>
            )}
          </div>
          
          <div className="max-w-full sm:max-w-[800px] mx-auto">
            {searchResult.answer || searchResult.isStreamingAnswer ? (
              <div className="prose prose-sm sm:prose-lg max-w-none text-text-primary">
                <ReactMarkdown
                 remarkPlugins={[remarkGfm]}
                 components={{
                   // Custom components for better styling
                   h1: ({children}) => <h1 className="text-lg sm:text-2xl font-bold text-text-primary mb-3 sm:mb-4">{children}</h1>,
                   h2: ({children}) => <h2 className="text-base sm:text-xl font-semibold text-text-primary mb-2 sm:mb-3 mt-4 sm:mt-6">{children}</h2>,
                   h3: ({children}) => <h3 className="text-sm sm:text-lg font-semibold text-text-primary mb-1.5 sm:mb-2 mt-3 sm:mt-4">{children}</h3>,
                   h4: ({children}) => <h4 className="text-sm sm:text-base font-semibold text-text-primary mb-1.5 sm:mb-2 mt-2 sm:mt-3">{children}</h4>,
                   p: ({children}) => <p className="text-text-primary mb-3 sm:mb-4 leading-relaxed text-sm sm:text-base">{children}</p>,
                   ul: ({children}) => <ul className="list-disc ml-4 sm:ml-6 mb-3 sm:mb-4 space-y-0.5 sm:space-y-1 text-text-primary">{children}</ul>,
                   ol: ({children}) => <ol className="list-decimal ml-4 sm:ml-6 mb-3 sm:mb-4 space-y-0.5 sm:space-y-1 text-text-primary">{children}</ol>,
                   li: ({children}) => <li className="text-text-primary leading-relaxed mb-0.5 sm:mb-1 text-sm sm:text-base">{children}</li>,
                   strong: ({children}) => <strong className="font-semibold text-text-primary">{children}</strong>,
                   em: ({children}) => <em className="italic text-text-primary">{children}</em>,
                   code: ({children}) => <code className="bg-bg-quaternary text-brand-primary px-1 py-0.5 rounded text-xs sm:text-sm">{children}</code>,
                   pre: ({children}) => <pre className="bg-bg-quaternary p-2 sm:p-4 rounded-md sm:rounded-lg overflow-x-auto mb-3 sm:mb-4 text-xs sm:text-sm">{children}</pre>,
                   blockquote: ({children}) => <blockquote className="border-l-2 sm:border-l-4 border-brand-primary pl-2 sm:pl-4 italic text-text-secondary mb-3 sm:mb-4 text-sm sm:text-base">{children}</blockquote>,
                   a: ({href, children}) => {
                     // Check if it's a citation link
                     const citationMatch = href?.match(/^#citation-(\d+)$/)
                     if (citationMatch) {
                       const citationNumber = parseInt(citationMatch[1])
                       // Only render citation button if source exists
                       const hasValidSource = searchResult.sources && searchResult.sources.find(s => s.id === citationNumber)
                       
                       if (hasValidSource) {
                         return (
                           <button
                             onMouseEnter={(e) => handleCitationClick(e, citationNumber)}
                             onMouseLeave={() => setShowCitation(false)}
                             onClick={(e) => handleCitationClick(e, citationNumber)}
                             className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-blue-600 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors cursor-pointer mx-0.5"
                             title={`Lihat sumber ${citationNumber}`}
                           >
                             {citationNumber}
                           </button>
                         )
                       } else {
                         // Don't render anything if no valid source
                         return null
                       }
                     }
                     return <a href={href} className="text-brand-primary hover:text-brand-secondary underline" target="_blank" rel="noopener noreferrer">{children}</a>
                   },
                   table: ({children}) => <div className="overflow-x-auto mb-3 sm:mb-4"><table className="min-w-full border border-border-secondary text-xs sm:text-sm">{children}</table></div>,
                   thead: ({children}) => <thead className="bg-bg-quaternary">{children}</thead>,
                   tbody: ({children}) => <tbody>{children}</tbody>,
                   tr: ({children}) => <tr className="border-b border-border-secondary">{children}</tr>,
                   th: ({children}) => <th className="px-2 sm:px-4 py-1.5 sm:py-2 text-left font-semibold text-text-primary text-xs sm:text-sm">{children}</th>,
                   td: ({children}) => <td className="px-2 sm:px-4 py-1.5 sm:py-2 text-text-primary text-xs sm:text-sm">{children}</td>
                 }}
               >
                 {processMarkdownWithCitations(searchResult.answer)}
               </ReactMarkdown>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-text-secondary text-sm sm:text-base">
                  Menunggu jawaban...
                </div>
              </div>
            )}
          </div>
          
          {/* Search Stats */}
          <div className="mt-2.5 sm:mt-6 pt-2.5 sm:pt-4 border-t border-border-secondary">
            <div className="flex flex-wrap gap-1.5 sm:gap-4 text-xs sm:text-sm text-text-secondary mb-2.5 sm:mb-6">
              <span className="flex items-center gap-1">
                <BarChart3 className="w-3 h-3" />
                {searchResult.sources?.length || 0} sumber
              </span>
              <span className="flex items-center gap-1">
                <ImageIcon className="w-3 h-3" />
                {searchResult.images?.length || 0} gambar
              </span>
              {searchResult.hasFallbackSources && (
                <span className="text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Menggunakan sumber fallback
                </span>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-3">
              <button
                onClick={onRetry}
                className="flex-1 sm:flex-none px-4 py-2 bg-bg-quaternary hover:bg-bg-secondary text-text-primary border border-border-primary rounded-lg transition-colors font-medium text-sm flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Cari Ulang
              </button>
              <button
                onClick={handleSearchMore}
                className="flex-1 sm:flex-none px-4 py-2 bg-brand-primary hover:bg-brand-secondary text-white rounded-lg transition-colors font-medium text-sm flex items-center justify-center gap-2"
              >
                <Search className="w-4 h-4" />
                Cari Lainnya
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Citation Popup */}
      {showCitation && selectedCitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-bg-tertiary rounded-lg sm:rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden border border-border-primary">
            <div className="flex items-center justify-between p-2.5 sm:p-6 border-b border-border-secondary">
              <h3 className="text-sm sm:text-lg font-bold text-text-primary">Referensi</h3>
              <button
                onClick={() => setShowCitation(false)}
                className="p-1 sm:p-2 text-text-secondary hover:text-text-primary hover:bg-bg-quaternary rounded-md sm:rounded-lg transition-colors"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
            <div className="p-2.5 sm:p-6 overflow-y-auto">
              <div className="flex items-start space-x-2 sm:space-x-3 mb-2.5 sm:mb-4">
                <div className="flex-shrink-0">
                  <img 
                    src={`https://www.google.com/s2/favicons?domain=${selectedCitation.domain}&sz=32`}
                    alt={`${selectedCitation.domain} favicon`}
                    className="w-4 h-4 sm:w-6 sm:h-6 rounded"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'block'
                    }}
                  />
                  <div className="w-4 h-4 sm:w-6 sm:h-6 bg-brand-primary/10 rounded flex items-center justify-center" style={{display: 'none'}}>
                    <Globe className="w-2.5 h-2.5 sm:w-4 sm:h-4 text-brand-primary" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs sm:text-base font-semibold text-text-primary mb-1">
                    {selectedCitation.title}
                  </div>
                  <div className="text-xs sm:text-sm text-text-secondary mb-2">
                    {selectedCitation.domain}
                  </div>
                  <a 
                    href={selectedCitation.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-xs sm:text-sm text-brand-primary hover:text-brand-secondary transition-colors"
                  >
                    <ExternalLink className="w-2.5 h-2.5 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                    Buka Sumber
                  </a>
                </div>
              </div>
              {selectedCitation.snippet && (
                <div className="bg-bg-quaternary rounded-md sm:rounded-lg p-2 sm:p-4">
                  <p className="text-xs sm:text-sm text-text-primary leading-relaxed">
                    {selectedCitation.snippet}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

SearchResultPage.propTypes = {
  query: PropTypes.string.isRequired,
  searchResult: PropTypes.object.isRequired,
  searchType: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired
}

export default SearchResultPage